import { checkIfAllInstancesOfVariantAddedToCart } from '../cart/updateATCButton';
import { API_BASE, SHOPIFY_APP_PROXY_LOGIN_URL } from '../../scripts/utils/api';

const esc = (s) => (s ? s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') : '');

// Haversine formula for calculating distance between two coordinates
const deg2rad = (deg) => deg * (Math.PI / 180);
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

if (!customElements.get('variant-selector')) {
  class VariantSelector extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.onVariantChange);

      this.sectionId = this.dataset.section;
      this.datasetUrl = this.dataset.url;

      this.availablePartnerLocations = [];
      this.userLocation = null;
      this.allStores = [];
      this.selectedStoreId = null;
      this.searchTerm = '';

      this.mediaGallery = document.querySelector('[data-media-gallery-id]');

      if (this.mediaGallery) {
        this.mediaGallerySectionId = this.mediaGallery.dataset.mediaGalleryId;
        this.swiperEl = this.mediaGallery.querySelector('.swiper:not([thumbsSlider])');
      }

      this.productForm = document.getElementById(`product-form-${this.sectionId}`);
      this.productFormSection = document.getElementById(`shopify-section-${this.sectionId}`);

      this.storeLocations = null;
      this.init();
      this.initSearchListener();
      this.initDrawerListeners();
      this.fetchStoreLocations();
    }

    init() {
      this.getSelectedOptions();
      this.getSelectedVariant();
      this.updateVariantStatuses();

      if (this.currentVariant) {
        this.fetchPartnerStoresAvailability();
        this.currentVariantId = this.currentVariant.id;
        this.fetchAvailability();
      }
    }

    // Fetch store data with coordinates from the store locator API
    fetchStoreLocations() {
      // Check sessionStorage cache first
      const cached = sessionStorage.getItem('StoreLocations');
      if (cached) {
        this.storeLocations = JSON.parse(cached);
        // Re-render if we already have stores
        if (this.allStores.length > 0) {
          this.renderStoreList(this.allStores);
        }
        return;
      }

      fetch(`${API_BASE}/getStores`)
        .then((response) => response.json())
        .then((data) => {
          this.storeLocations = data;
          sessionStorage.setItem('StoreLocations', JSON.stringify(data));
          // Re-render if we already have stores
          if (this.allStores.length > 0) {
            this.renderStoreList(this.allStores);
          }
        })
        .catch((err) => {
          console.warn('Failed to fetch store locations:', err);
        });
    }

    initSearchListener() {
      document.addEventListener('input', (e) => {
        if (e.target.matches('[data-store-search-input]')) {
          this.searchTerm = e.target.value.trim().toLowerCase();
          this.renderStoreList(this.allStores);
        }
      });
    }

    requestGeolocation() {
      if (!navigator.geolocation) return;

      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'denied') {
            const searchContainer = document.querySelector('[data-search-container]');
            if (searchContainer) searchContainer.classList.remove('hidden');
            return;
          }
          this.doGeolocation();
        });
      } else {
        this.doGeolocation();
      }
    }

    doGeolocation() {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          if (this.allStores.length > 0) {
            this.renderStoreList(this.allStores);
          }
        },
        () => {
          this.userLocation = null;
          const searchContainer = document.querySelector('[data-search-container]');
          if (searchContainer) searchContainer.classList.remove('hidden');
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      );
    }

    initDrawerListeners() {
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-find-store-near-me]')) {
          this.requestGeolocation();
        }
        if (e.target.closest('[data-toggle-search]')) {
          const container = document.querySelector('[data-search-container]');
          if (container) container.classList.toggle('hidden');
        }
      });
    }

    filterStores(stores) {
      if (!this.searchTerm) return stores;
      return stores.filter((store) => {
        const searchableText = `${store.name || ''} ${store.address?.address1 || ''} ${store.address?.city || ''} ${store.address?.zip || ''}`.toLowerCase();
        return searchableText.includes(this.searchTerm);
      });
    }

    calculateDistance(store) {
      if (!this.userLocation) return null;

      let lat = parseFloat(store.address?.latitude);
      let lng = parseFloat(store.address?.longitude);

      // If store doesn't have coordinates, try to find them in the storeLocations cache
      if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && this.storeLocations) {
        const storeName = (store.name || '').toUpperCase();
        const matchedLocation = this.storeLocations.find((loc) => {
          const locName = (loc.name || '').toUpperCase();
          return locName === storeName || locName.includes(storeName) || storeName.includes(locName);
        });

        if (matchedLocation?.address) {
          lat = parseFloat(matchedLocation.address.latitude);
          lng = parseFloat(matchedLocation.address.longitude);
        }
      }

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return haversineKm(this.userLocation.lat, this.userLocation.lng, lat, lng);
    }

    sortStores(stores) {
      return stores.slice().sort((a, b) => {
        // First: stores with stock (available > 0) come first
        const aHasStock = (a.available || 0) > 0;
        const bHasStock = (b.available || 0) > 0;
        if (aHasStock && !bHasStock) return -1;
        if (!aHasStock && bHasStock) return 1;

        // Second: sort by distance if available, otherwise alphabetically
        const aDist = a._distance ?? Infinity;
        const bDist = b._distance ?? Infinity;
        if (aDist !== bDist) return aDist - bDist;
        return (a.name || '').localeCompare(b.name || '', 'en');
      });
    }

    fetchPartnerStoresAvailability() {
      // remove last part of the sku to get the parent sku
      const parentSku = this.currentVariant.sku;
      if (!parentSku) return;
      const parentSkus = parentSku.split('.').slice(0, -1).join('.');
      const partnerStores = JSON.parse(document.querySelector('#partner-stores').textContent);

      const mappedPartnerStores = partnerStores.map((item) => ({
        available: 0,
        name: item['Navn'],
        address: {
          address1: item['Adresse'],
          address2: item['Adresse2'],
          city: item['By'],
          phone: item['Telefon'].toString(),
          zip: item['Postnr.'].toString(),
        },
        store_id: item['Butiksnr.'].toString(),
        reserve_item: item['RESERVE'] === 'Yes' ? 'true' : 'false',
        email: item['EMAIL'],
      }));

      const _this = this;

      fetch(`${SHOPIFY_APP_PROXY_LOGIN_URL}/fetch-int-service-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ endpoint: `/intersport/get-partner-stores-stock`, method: "POST", payload: { skus: parentSkus } }),
      })
        .then((response) => response.json())
        .then((stockInfo) => {
          const availableLocations = [];

          if (!stockInfo.products || !stockInfo.products.length) return;

          for (const variant of stockInfo.products) {
            if (availableLocations.filter((location) => location.sku === variant.sku).length === 0) {
              availableLocations.push({ sku: variant.sku, stores: [] });
            }
          }

          for (const store of mappedPartnerStores) {
            for (const variant of stockInfo.products) {
              if (store.store_id === variant.shopifyLocationId) {
                store.available = variant.quantity;
                store.sku = variant.sku;
                availableLocations.find((location) => location.sku === variant.sku).stores.push(store);
              }
            }
          }

          _this.availablePartnerLocations = availableLocations;
        });
    }

    onVariantChange() {
      this.getSelectedOptions();
      this.getSelectedVariant();
      this.updateVariantStatuses();

      if (this.currentVariant) {
        this.currentVariantId = this.currentVariant.id;
        this.updateURL();
        this.updateFormID();
        this.getSectionContent();
        this.fetchAvailability();
        this.updateMedia();
        this.updateGalleryLabels();
      }

      const event = new Event('variant-changed');
      this.dispatchEvent(event);
    }

    getSelectedOptions() {
      this.productFormSection.classList.add('section-loading');

      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }

    updateVariantStatuses() {
      const selectedOptionOneVariants = this.variantData.filter((variant) => this.querySelector(':checked').value === variant.option1);
      const inputWrappers = [...this.querySelectorAll('fieldset')];
      inputWrappers.forEach((option, index) => {
        if (index === 0) return;
        const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
        const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
        const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected).map((variantOption) => variantOption[`option${index + 1}`]);
        this.setInputAvailability(optionInputs, availableOptionInputsValue);
      });
    }

    setInputAvailability(listOfOptions, listOfAvailableOptions) {
      listOfOptions.forEach((input) => {
        if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
          input.classList.remove('disabled');
        } else {
          input.classList.add('disabled');
        }
      });
    }

    getVariantJSON() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }

    getSelectedVariant() {
      this.currentVariant = this.getVariantJSON().find((variant) => {
        const findings = !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
        if (findings) return variant;
      });
      return this.currentVariant;
    }

    updateURL() {
      if (!this.currentVariant) return;
      window.history.replaceState({}, '', `${this.datasetUrl}?variant=${this.currentVariantId}`);
    }

    updateFormID() {
      if (!this.productForm) return;

      const form_input = this.productForm.querySelector('input[name="id"]');
      form_input.value = this.currentVariantId;
    }

    async getSectionContent() {
      const requestedVariantId = this.currentVariantId;
      fetch(`${this.datasetUrl}?variant=${requestedVariantId}&section_id=${this.sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          if (this.currentVariantId !== requestedVariantId) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');

          this.updateSku(this.currentVariant?.sku);
          this.updateEAN(this.currentVariant?.barcode);
          if (html) {
            this.updatePrice(html);
            this.updateAddToCartButton(html);
            this.updateInventoryStatus(html);
          }
        });
    }

    fetchAvailability() {
      const requestedVariantId = this.currentVariantId;
      const availableLocationsUrl = `${API_BASE}/getAllInventoryLevels?variantId=${requestedVariantId}`;
      fetch(availableLocationsUrl)
        .then((response) => response.json())
        .then((data) => {
          if (this.currentVariantId !== requestedVariantId) return;

          this.updateAvailabilityContent(data);
          this.fetchReserveInStoreContent(data);
        });
    }

    fetchReserveInStoreContent(data) {
      const requestedVariantId = this.currentVariantId;
      const reserveInStoreUrl = `${this.datasetUrl}?variant=${requestedVariantId}`;
      fetch(reserveInStoreUrl)
        .then((response) => response.text())
        .then((responseText) => {
          if (this.currentVariantId !== requestedVariantId) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (html) {
            this.updateReserveInStoreContent(data, html);
          }
        })
        .finally(() => {
          this.productFormSection.classList.remove('section-loading');
        });
    }

    updateAvailabilityContent(data) {
      let stores = JSON.parse(JSON.stringify(data));
      const excludedStoreName = '1796 Web-Retur (Born Nordic)';
      const variantPartnerLocations = this.availablePartnerLocations.find((variant) => variant.sku == this.currentVariant.sku)?.stores || [];
      stores.push(...variantPartnerLocations);
      stores = stores.filter((store) => store.name !== excludedStoreName);

      // Store all stores for filtering/searching
      this.allStores = stores;

      const storeAvailabilityWrapper = document.querySelector('.store-availability-wrapper');
      const storeAvailabilityCountEls = document.querySelectorAll('.store-availability-count span');
      const storeAvailabilityCountParentEls = document.querySelectorAll('.store-availability-count:not(.unavailable)');
      const storeAvailabilityUnavailableEls = document.querySelectorAll('.store-availability-count.unavailable');

      const drawerTrigger = storeAvailabilityWrapper?.querySelector('[data-inventory-status]');
      const storeCount = stores.length;

      storeAvailabilityUnavailableEls.forEach((button) => {
        button.classList.add('hidden');
      });

      if (storeCount == 0) {
        storeAvailabilityCountParentEls.forEach((button) => {
          button.classList.add('hidden');
        });
        storeAvailabilityUnavailableEls.forEach((button) => {
          button.classList.remove('hidden');
        });
        drawerTrigger?.removeAttribute('data-open-drawer');
      } else {
        storeAvailabilityCountParentEls.forEach((button) => {
          button.classList.remove('hidden');
        });
        storeAvailabilityUnavailableEls.forEach((button) => {
          button.classList.add('hidden');
        });
        drawerTrigger?.setAttribute('data-open-drawer', 'store-availability-drawer');
      }

      if (storeAvailabilityCountEls.length > 0) {
        storeAvailabilityCountEls.forEach((button) => {
          button.textContent = storeCount;
        });
      }

      if (storeAvailabilityWrapper) {
        if (storeCount <= 0 && !this.currentVariant.available) {
          storeAvailabilityWrapper.classList.add('hidden');
        } else {
          storeAvailabilityWrapper.classList.remove('hidden');
        }
      }

      // Render the store list
      this.renderStoreList(stores);
    }

    renderStoreList(stores) {
      const drawerContentEl = document.querySelector('[data-component-id="store-availability-drawer"] [data-store-availability-drawer-content]');
      if (!drawerContentEl) return;

      const storesWithDistance = stores.map((store) => ({
        ...store,
        _distance: this.calculateDistance(store),
      }));

      const filteredStores = this.filterStores(storesWithDistance);
      const sortedStores = this.sortStores(filteredStores);

      if (sortedStores.length > 0) {
        const drawerContent = sortedStores.map((store, index) => {
          const storeId = `store-${index}`;
          const hasStock = (store.available || 0) > 0;
          const distanceText = store._distance !== null ? `${store._distance.toFixed(1)} km` : '';
          const isSelected = this.selectedStoreId === storeId;
          return `
            <li class="bg-background text-foreground rounded-lg overflow-hidden border ${isSelected ? 'border-foreground' : 'border-borders'} transition-all" data-store-id="${storeId}">
              <div class="flex items-center justify-between p-4" data-store-header>
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <input
                    type="radio"
                    name="store-selection"
                    id="${storeId}"
                    value="${storeId}"
                    class="w-5 h-5 text-foreground border-foreground focus:ring-foreground flex-shrink-0 accent-foreground cursor-pointer"
                    ${isSelected ? 'checked' : ''}
                  >
                  <label for="${storeId}" class="font-medium text-sm uppercase cursor-pointer leading-snug" data-store-label>${esc(store.name)}</label>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0 cursor-pointer ml-2" data-store-label>
                  ${distanceText ? `<span class="text-sm font-medium">${distanceText}</span>` : ''}
                  ${!isSelected && hasStock ? `<span class="w-2.5 h-2.5 rounded-full bg-success flex-shrink-0"></span>` : ''}
                </div>
              </div>

              <div class="store-details px-4 pb-4 ${isSelected ? '' : 'hidden'}" data-store-details>
                <div class="pl-8 space-y-1">
                  <address class="not-italic text-sm">
                    ${store.address?.address1 ? `<p>${esc(store.address.address1)}</p>` : ''}
                    ${store.address?.zip || store.address?.city ? `<p>${esc(store.address.zip || '')} ${esc(store.address.city || '')}</p>` : ''}
                    ${store.address?.phone && store.address.phone.replace(/\D/g, '').length > 2 ? `<p>${esc(store.address.phone)}</p>` : ''}
                  </address>

                  <div class="pt-2">
                    <button type="button" class="text-sm font-medium flex items-center justify-between w-full" data-toggle-hours>
                      <span>${window.storeAvailabilityStrings?.openingHours || 'Åbningstider'}</span>
                      <svg class="w-4 h-4 transition-transform" data-hours-chevron viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    ${store.opening_hours ? `
                      <div class="opening-hours hidden mt-2 text-sm whitespace-pre-line" data-opening-hours>
                        ${esc(store.opening_hours)}
                      </div>
                    ` : ''}
                  </div>

                  <div class="flex items-center justify-between pt-3">
                    <span class="text-sm">${window.storeAvailabilityStrings?.pickupTime || 'Afhentning inden for 48 timer'}</span>
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-medium">${hasStock ? (window.storeAvailabilityStrings?.inStock || 'På lager') : (window.storeAvailabilityStrings?.outOfStock || 'Ikke på lager')}</span>
                      ${hasStock ? `<span class="w-2.5 h-2.5 rounded-full bg-success flex-shrink-0"></span>` : `<span class="w-2.5 h-2.5 rounded-full bg-error flex-shrink-0"></span>`}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          `;
        });
        drawerContentEl.innerHTML = drawerContent.join('');

        this.attachStoreListeners(drawerContentEl);
      } else {
        drawerContentEl.innerHTML = `<li class="text-center text-grey-500 py-4">${window.storeAvailabilityStrings?.noStoresFound || 'Ingen butikker fundet'}</li>`;
      }
    }

    attachStoreListeners(container) {
      container.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('click', (e) => {
          const storeItem = e.target.closest('[data-store-id]');
          const storeId = storeItem.dataset.storeId;
          if (this.selectedStoreId === storeId) {
            e.target.checked = false;
            this.handleStoreSelection(null, container);
          } else {
            this.handleStoreSelection(storeId, container);
          }
        });
      });

      container.querySelectorAll('[data-store-label]').forEach((label) => {
        label.addEventListener('click', (e) => {
          const storeItem = label.closest('[data-store-id]');
          const storeId = storeItem.dataset.storeId;
          if (this.selectedStoreId === storeId) {
            this.handleStoreSelection(null, container);
          } else {
            this.handleStoreSelection(storeId, container);
          }
        });
      });

      container.querySelectorAll('[data-toggle-hours]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          const storeItem = button.closest('[data-store-id]');
          const hoursEl = storeItem.querySelector('[data-opening-hours]');
          const chevron = button.querySelector('[data-hours-chevron]');

          if (hoursEl) {
            hoursEl.classList.toggle('hidden');
            chevron?.classList.toggle('rotate-180');
          }
        });
      });

      container.querySelectorAll('[data-reserve-store]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          const storeName = button.dataset.reserveStore;
          const reserveDrawer = document.querySelector('[data-component-id="reserve-in-store-drawer"]');
          const storeSelect = reserveDrawer?.querySelector('[name="store"]');
          if (storeSelect) {
            const option = Array.from(storeSelect.options).find(opt => opt.value === storeName);
            if (option) {
              storeSelect.value = storeName;
              storeSelect.dispatchEvent(new Event('change'));
            }
          }
          const reserveDrawerTrigger = document.querySelector('[data-open-drawer="reserve-in-store-drawer"]');
          reserveDrawerTrigger?.click();
        });
      });
    }

    handleStoreSelection(storeId, container) {
      this.selectedStoreId = storeId;
      this.renderStoreList(this.allStores);
    }

    updateReserveInStoreContent(data, html) {
      // get stores that has emails and reserve_item is true
      const variantPartnerLocations = this.availablePartnerLocations.find((variant) => variant.sku == this.currentVariant.sku)?.stores || [];
      data.push(...variantPartnerLocations);

      const stores = data.filter((store) => store.email && store.reserve_item === 'true');

      const storeAvailabilityWrapper = document.querySelector('.store-availability-wrapper');
      const reserveInStoreButton = storeAvailabilityWrapper?.querySelector('[data-open-drawer="reserve-in-store-drawer"]');
      const reserveDrawerEl = document.querySelector('[data-component-id="reserve-in-store-drawer"]');
      if (!reserveDrawerEl) return;
      const reserveDrawer = html.querySelector('[data-component-id="reserve-in-store-drawer"]');
      if (!reserveDrawer) return;

      if (!stores) {
        if (reserveInStoreButton) reserveInStoreButton.classList.add('!hidden');
        return;
      }
      const storeCount = stores.length;

      // update store options
      const storesOptionsEl = reserveDrawerEl.querySelector('#FormReserveInStore [name="store"]');
      const placeholderOption = `<option value="" disabled selected>${window.storeAvailabilityStrings.selectStore}</option>`;
      if (storesOptionsEl) {
        storesOptionsEl.innerHTML = placeholderOption;
      }
      if (reserveInStoreButton && storeCount > 0) {
        if (reserveInStoreButton) reserveInStoreButton.classList.remove('!hidden');
        const sortedStores = stores.sort((a, b) => a.name.localeCompare(b.name));
        const storesOptions = sortedStores
          .map((store) => {
            return `<option value="${store.name}" email="${store.email}">${store.name}</option>`;
          })
          .join('');
        if (storesOptionsEl) {
          storesOptionsEl.insertAdjacentHTML('beforeend', storesOptions);
        }
      } else {
        if (reserveInStoreButton) reserveInStoreButton.classList.add('!hidden');
      }

      // update form data
      const formData = reserveDrawer.querySelector('#FormReserveInStore [data-reactive]');
      const formDataTarget = reserveDrawerEl.querySelector('#FormReserveInStore [data-reactive]');
      if (formData && formDataTarget) {
        formDataTarget.innerHTML = formData.innerHTML;
      }
    }

    updatePrice(html) {
      const id = `price-${this.sectionId}`;
      const oldPrice = document.getElementById(id);
      const newPrice = html.getElementById(id);
      if (oldPrice && newPrice) oldPrice.innerHTML = newPrice.innerHTML;
    }

    updateAddToCartButton(html) {
      const productForm = this.productForm;
      if (!productForm) return;

      const addButtonUpdated = html.getElementById(`Add-to-cart-${this.sectionId}`);
      const disable = addButtonUpdated?.hasAttribute('disabled') ?? true;

      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = addButton.querySelector('.add-to-cart-text');

      if (disable) {
        if (addButton) addButton.setAttribute('disabled', '');
        if (addButtonText) addButtonText.textContent = window.variantStrings.soldOut;
      } else {
        if (addButton) addButton.removeAttribute('disabled');
        if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    updateSku(newSku) {
      const id = 'Sku-placeholder';
      const oldSku = document.getElementById(id);
      if (!oldSku || !newSku) return;

      const skuValueEl = oldSku.querySelector('[data-value]');

      if (skuValueEl) skuValueEl.textContent = newSku;
    }

    updateEAN(newEAN) {
      if (!newEAN) return;

      const querySelector = '[data-product-ean]';
      const eanEL = document.querySelectorAll(querySelector);
      if (eanEL && eanEL.length > 0) {
        eanEL.forEach((el) => {
          el.textContent = newEAN;
        });
      }
    }

    updateInventoryStatus(html) {
      const id = `Inventory-status-${this.sectionId}`;
      const oldInventory = document.getElementById(id);
      const newInventory = html.getElementById(id);
      const buyBtn = document.querySelector('[data-total-available]');
      if (oldInventory && newInventory) oldInventory.innerHTML = newInventory.innerHTML;
      if (buyBtn && newInventory) buyBtn.dataset.totalAvailable = newInventory.dataset.count;
      checkIfAllInstancesOfVariantAddedToCart();
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.swiperEl) return;

      const activeSlide = this.swiperEl.querySelector(`[data-media-id="Slide-${this.currentVariant.featured_media.id}"]`);
      if (!activeSlide) return;
      const activeIndex = parseInt(activeSlide.getAttribute('data-swiper-slide-index'));
      const swiper = this.swiperEl.swiper;
      swiper.slideToLoop(activeIndex);
    }

    updateGalleryLabels() {
      if (!this.mediaGallery) return;

      this.soldOutLabels = this.mediaGallery.querySelectorAll('[data-sold-out-label]');
      this.salesOfferLabels = this.mediaGallery.querySelectorAll('[data-sales-offer-label]');

      if (this.currentVariant && this.currentVariant.available) {
        this.soldOutLabels.forEach((label) => {
          label.classList.add('hidden');
        });
      } else {
        this.soldOutLabels.forEach((label) => {
          label.classList.remove('hidden');
        });
      }

      if (this.currentVariant && this.currentVariant.compare_at_price) {
        this.salesOfferLabels.forEach((label) => {
          label.classList.remove('hidden');
        });
      } else {
        this.salesOfferLabels.forEach((label) => {
          label.classList.add('hidden');
        });
      }
    }
  }
  customElements.define('variant-selector', VariantSelector);
}
